import { combineReducers } from "redux";
import { reducer as formReducer } from "redux-form";

import { LOGOUT_USER } from "@/redux/action-types.ts";

import { aaFeatureReducer, type IAaFeatureState } from "./modules/aaFeature.ts";
import advancedSearchReducer, { type IAdvancedSearchState } from "./modules/advanced-search.ts";
import auth, { type IAuthState } from "./modules/auth.ts";
import blastReducer, { type IBlastState } from "./modules/blast.ts";
import comparisonReducer, { type IComparisonState } from "./modules/comparison.ts";
import configReducer, { type IConfigurationState } from "./modules/config.ts";
import contentBrowserReducer, { type IContentBrowserState } from "./modules/content-browser.ts";
import contigReducer, { type IContigState } from "./modules/contig.ts";
import contigSearchReducer, { type IContigSearchState } from "./modules/contig-search.ts";
import dashboardReducer, { type IDashboardState } from "./modules/dashboard.ts";
import dataEditingReducer, { type IDataEditingState } from "./modules/data-editing.ts";
import dataExportReducer, { type IDataExport } from "./modules/data-export.ts";
import datasetViewerReducer, { type IDatasetViewerState } from "./modules/dataset-viewer.ts";
import errorsReducer, { type IErrorsState } from "./modules/errors.ts";
import { eventReducer, type IEventState } from "./modules/event.ts";
import fileUploadReducer, { type IFilesUploadState } from "./modules/file-upload.ts";
import filesReducer, { type IFileState } from "./modules/files.ts";
import geneReducer, { type IGeneState } from "./modules/gene.ts";
import genomeReducer, { type IGenomeState } from "./modules/genome.ts";
import genomeBrowserReducer, { type IGenomeBrowserState } from "./modules/genome-browser.ts";
import jobReducer, { type IJobState } from "./modules/job.ts";
import listingReducer, { type IListingState } from "./modules/listing.ts";
import { type IMarkerState, markerReducer } from "./modules/marker.ts";
import mutationReducer, { type IMutationState } from "./modules/mutation.ts";
import navbarReducer, { type INavbarState } from "./modules/navbar.ts";
import ncbiAgentReducer, { type INCBIAgentPlaybookState } from "./modules/ncbi-admin.ts";
import nfPlaybookExecutionReducer, { type INFPlaybookExecutionState } from "./modules/nf-playbook-execution.ts";
import notificationsReducer, { type INotificationsState } from "./modules/notifications.tsx";
import { type INuFeatureState, nuFeatureReducer } from "./modules/nuFeature.ts";
import parentReducer, { type IParentState } from "./modules/parent.ts";
import playbookAdminReducer, { type IPlaybookAdminState } from "./modules/playbook-admin.ts";
import playbookExecutionReducer, { type IPlaybookExecutionState } from "./modules/playbook-execution.ts";
import projectReducer, { type IProjectState } from "./modules/project.ts";
import projectCapabilitesReducer, { type IProjectCapabilitiesListState } from "./modules/project-capabilities-list.ts";
import projectListReducer, { type IProjectListState } from "./modules/project-list.ts";
import proteinReducer, { type IProteinState } from "./modules/protein.ts";
import rolesAdminReducer, { type IRolesAdminState } from "./modules/roles-admin.ts";
import sampleReducer, { type ISampleState } from "./modules/sample.ts";
import sampleAdminReducer, { type ISampleAdminState } from "./modules/sample-admin.ts";
import sampleEditingReducer, { type ISampleEditingState } from "./modules/sample-editing.ts";
import savedGroupReducer, { type ISavedGroupState } from "./modules/saved-group.ts";
import savedViewReducer, { type SavedViewState } from "./modules/saved-view.ts";
import searchResults, { type ISearchResultState } from "./modules/search-results.ts";
import sequenceReducer, { type ISequenceState } from "./modules/sequence.ts";
import sequencesReducer, { type ISequencesState } from "./modules/sequences.ts";
import systemAdminReducer, { type ISystemAdminState } from "./modules/system-admin.ts";
import tableContextReducer, { type ITableContextState } from "./modules/table-context.ts";
import tableData, { type ITableDataState } from "./modules/table-data.ts";
import toolsAdminReducer, { type IToolAdminState } from "./modules/tools-admin.ts";
import tracingReducer, { type ITracingState } from "./modules/tracing.ts";
import transcriptReducer, { type ITranscriptState } from "./modules/transcript.ts";
import userGroupAdminReducer, { type IUserGroupAdminState } from "./modules/user-group-admin.ts";
import usersAdminReducer, { type IUsersAdminState } from "./modules/users-admin.ts";
import websocketReducer, { type IWebsocketState } from "./modules/websocket.ts";

// Beware: the actual attributes in the 'state' are defined by
// the attribute names in the appReducer definition!
export interface IAppState {
  advancedSearch: IAdvancedSearchState;
  auth: IAuthState;
  blast: IBlastState;
  config: IConfigurationState;
  contentBrowser: IContentBrowserState;
  contigSearch: IContigSearchState;
  contig: IContigState;
  sequence: ISequenceState;
  datasetViewer: IDatasetViewerState;
  errors: IErrorsState;
  filesUploadState: IFilesUploadState;
  gene: IGeneState;
  genomeBrowser: IGenomeBrowserState;
  job: IJobState;
  dataExport: IDataExport;
  notifications: INotificationsState;
  listing: IListingState;
  mutation: IMutationState;
  nfPlaybookExecution: INFPlaybookExecutionState;
  playbookAdminState: IPlaybookAdminState;
  playbookExecution: IPlaybookExecutionState;
  project: IProjectState;
  projectList: IProjectListState;
  protein: IProteinState;
  tableContext: ITableContextState;
  tableData: ITableDataState;
  transcript: ITranscriptState;
  genome: IGenomeState;
  rolesAdmin: IRolesAdminState;
  sample: ISampleState;
  parent: IParentState;
  sampleAdmin: ISampleAdminState;
  sampleEditingState: ISampleEditingState;
  sequencesState: ISequencesState;
  dataEditing: IDataEditingState;
  savedGroup: ISavedGroupState;
  savedView: SavedViewState;
  searchResults: ISearchResultState;
  userGroupAdmin: IUserGroupAdminState;
  usersAdmin: IUsersAdminState;
  comparison: IComparisonState;
  tracing: ITracingState;
  websocket: IWebsocketState;
  projectCapabilities: IProjectCapabilitiesListState;
  toolsAdmin: IToolAdminState;
  ncbiAgent: INCBIAgentPlaybookState;
  systemAdmin: ISystemAdminState;
  dashboardState: IDashboardState;
  navBar: INavbarState;
  form: any;
  files: IFileState;
  marker: IMarkerState;
  nuFeature: INuFeatureState;
  aaFeature: IAaFeatureState;
  event: IEventState;
}

const appReducer = () =>
  combineReducers({
    advancedSearch: advancedSearchReducer,
    auth: auth,
    blast: blastReducer,
    config: configReducer,
    contentBrowser: contentBrowserReducer,
    contigSearch: contigSearchReducer,
    contig: contigReducer,
    sequence: sequenceReducer,
    datasetViewer: datasetViewerReducer,
    errors: errorsReducer,
    filesUploadState: fileUploadReducer,
    gene: geneReducer,
    genomeBrowser: genomeBrowserReducer,
    job: jobReducer,
    notifications: notificationsReducer,
    listing: listingReducer,
    mutation: mutationReducer,
    project: projectReducer,
    projectList: projectListReducer,
    protein: proteinReducer,
    tableContext: tableContextReducer,
    tableData: tableData,
    transcript: transcriptReducer,
    genome: genomeReducer,
    rolesAdmin: rolesAdminReducer,
    sample: sampleReducer,
    parent: parentReducer,
    sampleAdmin: sampleAdminReducer,
    sampleEditingState: sampleEditingReducer,
    sequencesState: sequencesReducer,
    dataEditing: dataEditingReducer,
    savedGroup: savedGroupReducer,
    savedView: savedViewReducer,
    searchResults: searchResults,
    userGroupAdmin: userGroupAdminReducer,
    usersAdmin: usersAdminReducer,
    dataExport: dataExportReducer,
    comparison: comparisonReducer,
    tracing: tracingReducer,
    form: formReducer,
    websocket: websocketReducer,
    projectCapabilities: projectCapabilitesReducer,
    toolsAdmin: toolsAdminReducer,
    ncbiAgent: ncbiAgentReducer,
    systemAdmin: systemAdminReducer,
    navBar: navbarReducer,
    dashboardState: dashboardReducer,
    playbookAdminState: playbookAdminReducer,
    playbookExecution: playbookExecutionReducer,
    nfPlaybookExecution: nfPlaybookExecutionReducer,
    files: filesReducer,
    marker: markerReducer,
    nuFeature: nuFeatureReducer,
    aaFeature: aaFeatureReducer,
    event: eventReducer,
  });

const rootReducer = () => (state, action) => {
  if (action.type === LOGOUT_USER) {
    // reset the redux store on logout, keep only
    // the config: still valid
    const { config } = state;
    state = { config };
  }
  return appReducer()(state, action);
};

export default rootReducer;
